// NOSONAR
import CaseOwnerProfileModal from '@/components/config_components/CaseOwnerProfileModal/CaseOwnerProfileModal.vue';
import UserProfileModal from '@/components/config_components/UserProfileModal/UserProfileModal.vue';
import { userProfileModalMixin } from '@/components/config_components/UserProfileModal/user_profile_modal_mixin';
import {
  DEFAULT_GATE_ROUTE_CONFIG,
  EMPTY_ONBOARDING_BADGES,
  GATE_ROUTE_ALIASES,
  GATE_ROUTE_CONFIG
} from './config_sidebar_constants';
import { configSidebarComputed } from './config_sidebar_computed';
import { configSidebarMethods } from './config_sidebar_methods';

const ONBOARDING_BADGE_KEYS = Object.keys(EMPTY_ONBOARDING_BADGES);
const SIDEBAR_ROUTE_QUERY_KEYS = ['status', 'mode', 'journey', 'journey_label'];

function add_journey_count(counts, case_item) {
  const journey_key = String(case_item?.entity_type_id || case_item?.journey_tone || '').toLowerCase();
  if (journey_key) counts.set(journey_key, (counts.get(journey_key) || 0) + 1);
  return counts;
}

function normalizeBadgeValue(value) {
  return String(value || '').trim().toLowerCase();
}

function getCaseJourneyValues(case_item = {}) {
  const values = [
    case_item?.entity_type_id,
    case_item?.journey_tone,
    case_item?.journey_key,
    case_item?.journey
  ].map(normalizeBadgeValue).filter(Boolean);
  return values;
}

function getItemJourneyValues(item = {}) {
  const values = [
    item?.id,
    item?.label
  ].map(normalizeBadgeValue).filter(Boolean);
  return values;
}

function countCasesForJourneyItem(cases = [], item = {}) {
  const item_values = getItemJourneyValues(item);
  if (!item_values.length) return 0;

  return cases.filter((case_item) => {
    const case_values = getCaseJourneyValues(case_item);
    return case_values.some((value) => item_values.includes(value));
  }).length;
}

const buildOnboardingCountBadges = (cases, entity_type_items = []) => {
  const journey_counts = cases.reduce(add_journey_count, new Map());
  const static_badges = ONBOARDING_BADGE_KEYS.reduce((badges, key) => {
    const count = ['my-queue', 'all-journeys'].includes(key) ? cases.length : journey_counts.get(key) || 0;
    return { ...badges, [key]: String(count) };
  }, {});

  return entity_type_items.reduce((badges, item) => {
    if (item.id === 'all-journeys') {
      return badges;
    }

    return {
      ...badges,
      [item.id]: String(journey_counts.get(String(item.id)) || countCasesForJourneyItem(cases, item))
    };
  }, static_badges);
};

const slugifyGateKey = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

const normalizeGateKey = (value) => slugifyGateKey(value);

const resolveGateRouteKey = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';

  const alias = GATE_ROUTE_ALIASES.find((item) => item.match(normalized));
  return alias?.gate_id || normalizeGateKey(value);
};

const toGateLabel = (value, fallback = 'Gate') => {
  const label = String(value || '').trim();
  if (!label) return fallback;
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const getStatusDisplayName = (status = {}) => {
  const candidates = [
    status?.status_name,
    status?.workflow_status_name,
    status?.status?.status,
    status?.status?.name,
    status?.name,
    status?.workflow_status,
    status?.status
  ];
  const value = candidates.find((candidate) => typeof candidate === 'string' && candidate.trim());
  return String(value || '').trim();
};

const getGateRouteQuery = (route_config, raw_label, gate_id) => {
  if (route_config.route_name === 'OnboardingManageVerification') {
    return { mode: gate_id || 'verification' };
  }

  return route_config.route_query
    || (route_config.route_name === 'OnboardingGateStatusQueue' ? { status: raw_label || gate_id } : undefined);
};

const countCasesForGate = (cases = [], gate_id = '') => {
  const normalized_gate_id = normalizeGateKey(gate_id);
  if (!normalized_gate_id) return 0;

  return cases.filter((case_item) => {
    const stage_values = [
      case_item?.stage,
      case_item?.detail,
      case_item?.current_workflow_status,
      case_item?.workflow_display_stage,
      case_item?.activation,
      case_item?.status,
      case_item?.workflow_status_name,
      case_item?.workflow_status
    ];
    return stage_values.some((stage_value) => normalizeGateKey(stage_value) === normalized_gate_id);
  }).length;
};

function getGateCount(status, cases, gate_id) {
  if (Array.isArray(cases)) {
    return String(countCasesForGate(cases, gate_id));
  }

  const status_count = Number(status?.case_count);
  return String(Number.isFinite(status_count) ? status_count : 0);
}

function isPositiveNumber(value) {
  if (!Number.isFinite(value)) return false;
  return value > 0;
}

function getGateOrder(status = {}) {
  const order_values = [
    Reflect.get(status, 'order'),
    Reflect.get(status, 'workflow_status_order'),
    Reflect.get(status, 'display_order')
  ];
  const order = order_values.map(Number).find(isPositiveNumber);
  return order || null;
}

const getMergedGateOrder = (existing_order, next_order) => {
  if (existing_order && next_order) return Math.min(existing_order, next_order);
  return existing_order || next_order || null;
};

const buildGateItem = (status = {}, cases = []) => {
  const raw_label = getStatusDisplayName(status);
  const gate_id = normalizeGateKey(raw_label || status?.id);
  if (!gate_id) return null;

  const route_key = resolveGateRouteKey(raw_label || status?.id);
  const route_config = GATE_ROUTE_CONFIG.get(route_key) || DEFAULT_GATE_ROUTE_CONFIG;
  return Object.fromEntries([
    ['id', gate_id],
    ['label', toGateLabel(raw_label, toGateLabel(gate_id.replace(/-/g, ' ')))],
    ['order', getGateOrder(status)],
    ['route_name', route_config.route_name],
    ['route_query', getGateRouteQuery(route_config, raw_label, gate_id)],
    ['icon', route_config.icon],
    ['tone', route_config.tone],
    ['count', getGateCount(status, cases, gate_id)]
  ]);
};

const mergeGateItems = (gate_map, gate_item) => {
  if (!gate_item) return;
  const existing_item = gate_map.get(gate_item.id);
  if (!existing_item) {
    gate_map.set(gate_item.id, gate_item);
    return;
  }

  gate_map.set(gate_item.id, {
    ...existing_item,
    order: getMergedGateOrder(existing_item.order, gate_item.order),
    count: String(Math.max(Number(existing_item.count || 0), Number(gate_item.count || 0)))
  });
};

const sortGateItems = (left, right) => {
  const left_order = Number(left?.order);
  const right_order = Number(right?.order);
  const left_has_order = Number.isFinite(left_order) && left_order > 0;
  const right_has_order = Number.isFinite(right_order) && right_order > 0;
  if (left_has_order && right_has_order && left_order !== right_order) return left_order - right_order;
  if (left_has_order !== right_has_order) return left_has_order ? -1 : 1;
  return String(left?.label || '').localeCompare(String(right?.label || ''));
};

export default {
  name: 'ConfigGlobalSidebar',
  mixins: [userProfileModalMixin],
  components: {
    CaseOwnerProfileModal,
    UserProfileModal
  },
  data: () => ({
    show_case_owner_profile_modal: false,
    current_journey_menu_open: false,
    onboarding_count_badges: {},
    onboarding_journey_count_badges: {},
    onboarding_entity_type_items: [],
    onboarding_gate_items: [],
    latest_onboarding_sidebar_cases: [],
    selected_onboarding_case_scope: null,
    _sidebar_gate_load_token: 0,
    SIDEBAR_ROUTE_QUERY_KEYS,
    _build_onboarding_count_badges: buildOnboardingCountBadges,
    _build_gate_item: buildGateItem,
    _merge_gate_items: mergeGateItems,
    _sort_gate_items: sortGateItems
  }),
  computed: {
    ...configSidebarComputed,
  },
  mounted() {
    if (this.uses_onboarding_system_sidebar) this.sync_current_journey_filter_from_route();
    if (this.uses_onboarding_system_sidebar) this.load_onboarding_sidebar_counts();
    window.addEventListener('onboarding-data-updated', this.handle_onboarding_data_updated);
    window.addEventListener('onboarding-sidebar-cases-loaded', this.handle_onboarding_sidebar_cases_loaded);
    window.addEventListener('onboarding-case-scope-selected', this.handle_onboarding_case_scope_selected);
    window.addEventListener('onboarding-case-scope-cleared', this.handle_onboarding_case_scope_cleared);
  },
  beforeUnmount() {
    window.removeEventListener('onboarding-data-updated', this.handle_onboarding_data_updated);
    window.removeEventListener('onboarding-sidebar-cases-loaded', this.handle_onboarding_sidebar_cases_loaded);
    window.removeEventListener('onboarding-case-scope-selected', this.handle_onboarding_case_scope_selected);
    window.removeEventListener('onboarding-case-scope-cleared', this.handle_onboarding_case_scope_cleared);
  },
  watch: {
    uses_onboarding_system_sidebar(is_onboarding_sidebar) {
      if (is_onboarding_sidebar && !Object.keys(this.onboarding_count_badges).length) this.load_onboarding_sidebar_counts();
    },
    '$route.query.journey'() {
      this.sync_current_journey_filter_from_route();
      if (this.uses_onboarding_system_sidebar) this.load_onboarding_sidebar_counts();
    },
    '$route.query.journey_label'() {
      this.sync_current_journey_filter_from_route();
    },
    '$route.params.journeyId'() {
      this.sync_current_journey_filter_from_route();
      if (this.uses_onboarding_system_sidebar) this.load_onboarding_sidebar_counts();
    }
  },
  methods: {
    ...configSidebarMethods
  }
};
